import { Buffer } from 'buffer';
import { NextRouter, useRouter } from 'next/router';
import { useOnboarder } from './OnboarderContext';
import type { Step } from './Config';

export class OnboarderNav<Data> {
    router: NextRouter;
    step: string;
    basePath: string;
    steps: Step<Data>[];

    constructor(router: NextRouter, step: string, basePath: string, steps: Step<Data>[]) {
        this.router = router;
        this.basePath = basePath;
        this.steps = steps;
        this.step = step;
    }
    currentStepIndex(): number {
        if (!this.step) {
            return -1;
        }
        return parseInt(this.step, 10) - 1;
    }
    goTo(path: string): void {
        this.router.push(path, undefined, {
            shallow: true,
        });
    }
    currentStep(): Step<Data> {
        return this.steps[this.currentStepIndex()];
    }
    goToLauncher(): void {
        this.goTo(this.basePath);
    }
    goToRecommendation(data: Data): void {
        const urlData = Buffer.from(JSON.stringify(data), 'utf8').toString('base64');
        this.goTo(`${this.basePath}/recommendation/${encodeURIComponent(urlData)}`);
    }
    goToStepByIndex(index: number): void {
        const step = this.steps[index];
        this.goTo(`${this.basePath}/${index + 1}/${step.key}`);
    }
    goToNextStep(): void {
        if (this.isLastStep()) {
            return;
        }

        this.goToStepByIndex(this.currentStepIndex() + 1);
    }
    goToPreviousStep(): void {
        if (this.isFirstStep()) {
            this.goToLauncher();
            return;
        }
        this.goToStepByIndex(this.currentStepIndex() - 1);
    }
    goToLastStep(): void {
        this.goToStepByIndex(this.totalSteps() - 1);
    }
    isLastStep(): boolean {
        return this.currentStepIndex() >= this.totalSteps() - 1;
    }
    isFirstStep(): boolean {
        return this.currentStepIndex() === 0;
    }
    totalSteps(): number {
        return this.steps.length;
    }
}

export function useOnboarderNav() {
    const {
        config: { basePath },
        steps,
    } = useOnboarder();
    const router = useRouter();
    const [step] = router.query.params || [];

    return new OnboarderNav(router, step, basePath, steps);
}
